/**
 * Jikan API Service
 * MyAnimeList unofficial API for anime metadata
 * https://docs.api.jikan.moe/
 */

const JIKAN_BASE_URL = 'https://api.jikan.moe/v4'

// レート制限対策: リクエスト間隔を管理
let lastRequestTime = 0
const MIN_REQUEST_INTERVAL = 400 // 400ms間隔 (Jikanは1秒3リクエストまで)

/**
 * レート制限を考慮したfetch
 */
async function rateLimitedFetch(url) {
  const now = Date.now()
  const timeSinceLastRequest = now - lastRequestTime

  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await new Promise(resolve =>
      setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest)
    )
  }

  lastRequestTime = Date.now()

  const response = await fetch(url)

  if (!response.ok) {
    if (response.status === 429) {
      // レート制限に達した場合、1秒待ってリトライ
      await new Promise(resolve => setTimeout(resolve, 1000))
      return rateLimitedFetch(url)
    }
    throw new Error(`Jikan API error: ${response.status}`)
  }

  return response.json()
}

/**
 * アニメを検索
 * @param {string} query 検索クエリ
 * @returns {Promise<Array>} 検索結果
 */
export async function searchAnime(query) {
  if (!query || query.trim().length === 0) {
    return []
  }

  try {
    const encodedQuery = encodeURIComponent(query.trim())
    const url = `${JIKAN_BASE_URL}/anime?q=${encodedQuery}&limit=5&sfw=true`

    const data = await rateLimitedFetch(url)
    return data.data || []
  } catch (error) {
    console.error('Jikan searchAnime error:', error)
    return []
  }
}

/**
 * アニメの詳細情報を取得
 * @param {number} malId MyAnimeList ID
 * @returns {Promise<Object|null>} アニメ詳細
 */
export async function getAnimeDetails(malId) {
  if (!malId) return null

  try {
    const url = `${JIKAN_BASE_URL}/anime/${malId}/full`
    const data = await rateLimitedFetch(url)
    return data.data || null
  } catch (error) {
    console.error('Jikan getAnimeDetails error:', error)
    return null
  }
}

/**
 * アニメのスタッフ情報を取得
 * @param {number} malId MyAnimeList ID
 * @returns {Promise<Object|null>} スタッフ情報
 */
export async function getAnimeStaff(malId) {
  if (!malId) return null

  try {
    const url = `${JIKAN_BASE_URL}/anime/${malId}/staff`
    const data = await rateLimitedFetch(url)
    return data.data || null
  } catch (error) {
    console.error('Jikan getAnimeStaff error:', error)
    return null
  }
}

/**
 * アニメのメタデータを取得（検索 → 詳細取得）
 * @param {string} title アニメタイトル
 * @returns {Promise<Object|null>} メタデータ
 */
export async function fetchAnimeMetadata(title) {
  if (!title) return null

  try {
    // まず検索して候補を取得
    const searchResults = await searchAnime(title)

    if (searchResults.length === 0) {
      return null
    }

    // 最初の結果を使用（最も関連性が高いと仮定）
    const bestMatch = searchResults[0]

    // 詳細情報を取得
    const details = await getAnimeDetails(bestMatch.mal_id)

    if (!details) {
      return formatBasicMetadata(bestMatch)
    }

    // スタッフ情報も取得
    const staffData = await getAnimeStaff(bestMatch.mal_id)

    return formatFullMetadata(details, staffData)
  } catch (error) {
    console.error('Jikan fetchAnimeMetadata error:', error)
    return null
  }
}

/**
 * 基本メタデータをフォーマット
 */
function formatBasicMetadata(animeData) {
  return {
    malId: animeData.mal_id,
    title: animeData.title,
    titleJapanese: animeData.title_japanese,
    imageUrl: animeData.images?.jpg?.image_url || null,
    episodes: animeData.episodes,
    status: animeData.status,
    airing: animeData.airing,
    aired: animeData.aired,
    broadcast: animeData.broadcast,
    score: animeData.score,
    synopsis: animeData.synopsis,
    studios: animeData.studios?.map(s => s.name) || [],
    genres: animeData.genres?.map(g => g.name) || []
  }
}

/**
 * 詳細メタデータをフォーマット
 */
function formatFullMetadata(details, staffData) {
  const metadata = formatBasicMetadata(details)

  // スタッフ情報を追加
  if (staffData && Array.isArray(staffData)) {
    // 監督を探す
    const director = staffData.find(s =>
      s.positions?.some(p =>
        p.toLowerCase().includes('director') && !p.toLowerCase().includes('episode')
      )
    )

    // 主要声優を取得
    const mainCast = details.characters?.slice(0, 5)?.map(c => ({
      character: c.character?.name,
      voiceActor: c.voice_actors?.find(va => va.language === 'Japanese')?.person?.name
    })) || []

    metadata.director = director?.person?.name || null
    metadata.mainCast = mainCast
  }

  return metadata
}

/**
 * 放送スケジュールから次回放送日を計算
 * @param {Object} broadcast 放送情報
 * @returns {Object|null} 次回放送情報
 */
export function parseSchedule(broadcast) {
  if (!broadcast || !broadcast.day) {
    return null
  }

  const dayMap = {
    'Sundays': 0,
    'Mondays': 1,
    'Tuesdays': 2,
    'Wednesdays': 3,
    'Thursdays': 4,
    'Fridays': 5,
    'Saturdays': 6
  }

  const broadcastDay = dayMap[broadcast.day]
  if (broadcastDay === undefined) {
    return null
  }

  // 放送時間を解析（例: "23:00 (JST)"）
  let broadcastHour = 0
  let broadcastMinute = 0

  if (broadcast.time) {
    const timeMatch = broadcast.time.match(/(\d{1,2}):(\d{2})/)
    if (timeMatch) {
      broadcastHour = parseInt(timeMatch[1], 10)
      broadcastMinute = parseInt(timeMatch[2], 10)
    }
  }

  return {
    dayOfWeek: broadcastDay,
    dayName: broadcast.day,
    time: broadcast.time || null,
    hour: broadcastHour,
    minute: broadcastMinute,
    timezone: broadcast.timezone || 'Asia/Tokyo'
  }
}

export default {
  searchAnime,
  getAnimeDetails,
  getAnimeStaff,
  fetchAnimeMetadata,
  parseSchedule
}
